import React from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const PrivacyPolicy = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Privacy Policy — Engaged Manila"
        description="Read the Engaged Manila privacy policy. Learn how we collect, use, and protect your data on our influencer marketing platform."
        url="/privacy-policy"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-4xl sm:text-5xl font-extrabold text-gray-900 tracking-tight">
              Privacy <span className="text-primary">Policy</span>
            </h1>
            <p className="mt-4 max-w-3xl mx-auto text-xl text-gray-500">
              Last updated: August 1, 2025
            </p>
          </motion.div>
        </div>
      </section>
      
      {/* Content Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Table of Contents */}
          <motion.div
            className="mb-12 bg-gray-50 rounded-xl p-6 border border-gray-200 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Table of Contents</h2>
            <nav className="grid md:grid-cols-2 gap-2">
              {[
                { id: "introduction", title: "1. Introduction" },
                { id: "information-we-collect", title: "2. Information We Collect" },
                { id: "how-we-use", title: "3. How We Use Your Information" },
                { id: "sharing", title: "4. Sharing Your Information" },
                { id: "data-security", title: "5. Data Security" },
                { id: "your-rights", title: "6. Your Data Protection Rights" },
                { id: "cookies", title: "7. Cookies and Tracking Technologies" },
                { id: "childrens-privacy", title: "8. Children's Privacy" },
                { id: "changes", title: "9. Changes to This Privacy Policy" },
                { id: "contact", title: "10. Contact Us" }
              ].map(item => (
                <a 
                  key={item.id} 
                  href={`#${item.id}`} 
                  className="flex items-center py-2 px-3 text-gray-700 hover:bg-gray-100 rounded-md group transition-colors duration-200"
                >
                  <span>{item.title}</span>
                </a>
              ))}
            </nav>
          </motion.div>
          
          <motion.div 
            className="bg-white rounded-xl p-8 shadow-md border border-gray-100 prose prose-lg max-w-none"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
          <motion.div variants={itemVariants} id="introduction" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Engaged Manila ("we," "our," or "us") respects your privacy and is committed to protecting your personal data. 
              This privacy policy will inform you about how we look after your personal data when you visit our website and 
              use our services, and tell you about your privacy rights and how the law protects you.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              This privacy policy applies to all information collected through our website, mobile application, 
              and/or any related services, sales, marketing, or events (collectively, the "Services").
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="information-we-collect" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">2. Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We collect several types of information from and about users of our Services, including:
            </p>
            <ul className="space-y-3 list-disc pl-6">
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Personal Data:</strong> Personal data refers to any information that identifies or can be used to 
                identify an individual. We collect personal data that you voluntarily provide to us when you register on 
                the Services, express an interest in obtaining information about us or our products and Services, participate 
                in activities on the Services, or otherwise contact us.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Account Information:</strong> If you create an account with us, we collect your name, email address, 
                username, password, profile picture, and other information you choose to provide.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Profile Information:</strong> For influencers, we collect information about your social media accounts, 
                follower counts, engagement rates, content categories, and demographic information about your audience.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Transaction Information:</strong> If you make a purchase or engage in a transaction through our Services, 
                we collect information about the transaction, such as product details, purchase amount, and date of the transaction.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Automatically Collected Information:</strong> We automatically collect certain information when you visit, 
                use, or navigate the Services. This information does not reveal your specific identity but may include device and 
                usage information, such as your IP address, browser and device characteristics, operating system, language preferences, 
                referring URLs, device name, country, location, information about how and when you use our Services, and other technical information.
              </li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="how-we-use" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect in various ways, including to:
            </p>
            <ul className="space-y-3 list-disc pl-6">
              <li className="text-gray-700">Provide, operate, and maintain our Services</li>
              <li className="text-gray-700">Improve, personalize, and expand our Services</li>
              <li className="text-gray-700">Understand and analyze how you use our Services</li>
              <li className="text-gray-700">Develop new products, services, features, and functionality</li>
              <li className="text-gray-700">Communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the Services, and for marketing and promotional purposes</li>
              <li className="text-gray-700">Process your transactions</li>
              <li className="text-gray-700">Find and prevent fraud</li>
              <li className="text-gray-700">Match brands with appropriate influencers based on campaign requirements and influencer profiles</li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="sharing" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">4. Sharing Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may share your information with:
            </p>
            <ul className="space-y-3 list-disc pl-6">
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Brands and Influencers:</strong> We share information between brands and influencers as necessary to facilitate campaigns and collaborations.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Service Providers:</strong> We may share your information with third-party vendors, service providers, contractors, or agents who perform services for us or on our behalf.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">Business Transfers:</strong> We may share or transfer your information in connection with, or during negotiations of, any merger, sale of company assets, financing, or acquisition of all or a portion of our business to another company.
              </li>
              <li className="text-gray-700">
                <strong className="text-gray-900 font-semibold">With Your Consent:</strong> We may disclose your personal information for any other purpose with your consent.
              </li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="data-security" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="your-rights" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">6. Your Data Protection Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Depending on your location, you may have the following data protection rights:
            </p>
            <ul className="space-y-3 list-disc pl-6">
              <li className="text-gray-700">The right to access, update, or delete the information we have on you</li>
              <li className="text-gray-700">The right of rectification - the right to have your information corrected if it is inaccurate or incomplete</li>
              <li className="text-gray-700">The right to object to our processing of your personal data</li>
              <li className="text-gray-700">The right of restriction - the right to request that we restrict the processing of your personal information</li>
              <li className="text-gray-700">The right to data portability - the right to request that we provide you with a copy of the personal information we have about you in a structured, machine-readable format</li>
              <li className="text-gray-700">The right to withdraw consent - the right to withdraw your consent at any time where we relied on your consent to process your personal information</li>
            </ul>
          </motion.div>
          
          <motion.div variants={itemVariants} id="cookies" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies and similar tracking technologies to track the activity on our Services and store certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="childrens-privacy" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">8. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our Services are not intended for individuals under the age of 18. We do not knowingly collect personal information from children under 18. If you are a parent or guardian and you are aware that your child has provided us with personal information, please contact us so that we can take necessary actions.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="changes" className="mb-12 pb-8 border-b border-gray-200">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">9. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may update our privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last updated" date at the top of this privacy policy.
            </p>
          </motion.div>
          
          <motion.div variants={itemVariants} id="contact" className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">10. Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about this privacy policy, please contact us:
            </p>
            <ul className="space-y-3 list-disc pl-6">
              <li className="text-gray-700">By email: hola@engagedmanila.com</li>
              <li className="text-gray-700">By phone: +632 835 26129</li>
              <li className="text-gray-700">By mail: 785 Aurora Boulevard, Cubao, Quezon City, Metro Manila, Philippines</li>
            </ul>
          </motion.div>
          </motion.div>
          
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              This privacy policy was last updated on August 1, 2025.
              <br />
              © {new Date().getFullYear()} Engaged Manila. All rights reserved.
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
